import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback } from 'react';
import { ProbabilityOfBug } from '@atlassian/commerce-telemetry';
import { Breadcrumb, useGasV3TrackEventDispatch, useSentryExceptionDispatch,
// TODO: Unify method name terminology
useGasV3UIEventDispatch as useUIEventDispatch } from '@atlassian/commerce-telemetry/dispatch-hooks';
import { createEventChannel } from '@atlassian/commerce-telemetry/events';
import { CC_FORM } from '../../constants/breadcrumb-names';
import { calcTextFieldActionSubjectId, capitalizeFirstLetter } from '../action-subject-id';
import { ErrorTypes, isPaymentAuthenticationFailureResult, isSystemErrorResult } from '../errors';
import { isError, isException, isFailure, isSuccessful } from '../resultful';
export { Breadcrumb, useUIEventDispatch };
var fieldErrorEventChannel = createEventChannel();
var FieldErrorEventListener = fieldErrorEventChannel.Listener,
  useBaseFieldErrorEventDispatch = fieldErrorEventChannel.useEventDispatch;
export { FieldErrorEventListener, useBaseFieldErrorEventDispatch };
var _createEventChannel = createEventChannel(),
  ConfirmCardSetupResultListener = _createEventChannel.Listener,
  useBaseConfirmCardSetupResultEventDispatch = _createEventChannel.useEventDispatch;
export { ConfirmCardSetupResultListener, useBaseConfirmCardSetupResultEventDispatch };
var _createEventChannel2 = createEventChannel(),
  ConfirmCardPaymentResultListener = _createEventChannel2.Listener,
  useBaseConfirmCardPaymentResultEventDispatch = _createEventChannel2.useEventDispatch;
export { ConfirmCardPaymentResultListener, useBaseConfirmCardPaymentResultEventDispatch };
var _createEventChannel3 = createEventChannel(),
  CreatePaymentMethodResultListener = _createEventChannel3.Listener,
  useBaseCreatePaymentMethodEventDispatch = _createEventChannel3.useEventDispatch;
export { CreatePaymentMethodResultListener, useBaseCreatePaymentMethodEventDispatch };
var errorBoundaryEventChannel = createEventChannel();
var ErrorBoundaryListener = errorBoundaryEventChannel.Listener,
  useBaseErrorBoundaryEventDispatch = errorBoundaryEventChannel.useEventDispatch;
export { ErrorBoundaryListener, useBaseErrorBoundaryEventDispatch };
var getSentryExceptionFromResult = function getSentryExceptionFromResult(result) {
  if (isError(result)) {
    return result.error;
  } else if (isException(result)) {
    return result.exception;
  } else {
    return {
      actualValue: result,
      message: 'Sentry telemetry error: Result was not an error/exception'
    };
  }
};
var mapPaymentIntentToTags = function mapPaymentIntentToTags(paymentIntent) {
  return {
    'paymentIntent.id': paymentIntent.id,
    'paymentIntent.amount': "".concat(paymentIntent.amount),
    'paymentIntent.cancellationReason': "".concat(paymentIntent.cancellation_reason),
    'paymentIntent.captureMethod': paymentIntent.capture_method,
    'paymentIntent.clientToken': "".concat(paymentIntent.client_secret),
    'paymentIntent.confirmationMethod': paymentIntent.confirmation_method,
    'paymentIntent.currency': paymentIntent.currency,
    'paymentIntent.status': paymentIntent.status
  };
};
var mapSetupIntentToSentryTags = function mapSetupIntentToSentryTags(setupIntent) {
  var _setupIntent$next_act;
  return {
    'setupIntent.id': setupIntent.id,
    'setupIntent.cancellationReason': "".concat(setupIntent.cancellation_reason),
    'setupIntent.clientToken': "".concat(setupIntent.client_secret),
    'setupIntent.nextAction.type': "".concat((_setupIntent$next_act = setupIntent.next_action) === null || _setupIntent$next_act === void 0 ? void 0 : _setupIntent$next_act.type),
    'setupIntent.status': setupIntent.status,
    'setupIntent.usage': setupIntent.usage
  };
};
var mapStripeErrorToSentryTags = function mapStripeErrorToSentryTags(stripeError) {
  return _objectSpread(_objectSpread(_objectSpread({
    'stripeError.code': "".concat(stripeError.code)
  }, stripeError.setup_intent !== undefined ? mapSetupIntentToSentryTags(stripeError.setup_intent) : undefined), stripeError.payment_intent !== undefined ? mapPaymentIntentToTags(stripeError.payment_intent) : undefined), stripeError.payment_method !== undefined ? mapPaymentMethodToSentryTags(stripeError.payment_method) : undefined);
};
var mapPaymentMethodToSentryTags = function mapPaymentMethodToSentryTags(paymentMethod) {
  var _paymentMethod$card$t;
  return paymentMethod.card !== undefined ? {
    'paymentMethod.card.brand': paymentMethod.card.brand,
    'paymentMethod.card.country': "".concat(paymentMethod.card.country),
    'paymentMethod.card.threeDSecureUsage.supported': "".concat((_paymentMethod$card$t = paymentMethod.card.three_d_secure_usage) === null || _paymentMethod$card$t === void 0 ? void 0 : _paymentMethod$card$t.supported),
    'paymentMethod.card.funding': paymentMethod.card.funding
  } : {};
};
var createUseSubmissionTelemetryDispatch = function createUseSubmissionTelemetryDispatch(_ref) {
  var useBaseEventDispatch = _ref.useBaseEventDispatch,
    action = _ref.action,
    actionSubject = _ref.actionSubject;
  var task = "".concat(action).concat(capitalizeFirstLetter(actionSubject));
  var useSubmissionTelemetryDispatch = function useSubmissionTelemetryDispatch() {
    var dispatchSentryEvent = useSentryExceptionDispatch();
    var dispatchGasV3TrackEvent = useGasV3TrackEventDispatch();
    var eventDispatch = useBaseEventDispatch();
    var telemetryDispatch = useCallback(function (eventPayload) {
      var _result$payload;
      var result = eventPayload.timedPayload;

      // TODO: Rename this to isSuccess once https://atlassian.slack.com/archives/C016U73MTC1/p1617695955289900 Stripe issue is resolved. Required to gather impact of this bug.
      var isSuccessWithoutAccountingForNextActionBug = isSuccessful(result);
      // TODO: Remove once https://atlassian.slack.com/archives/C016U73MTC1/p1617695955289900 is fixed
      // It should be impossible to have next_action since handleActions is set to true in our package but it's still happening because of
      var isImpossibleSuccessState = isSuccessWithoutAccountingForNextActionBug && !!((_result$payload = result.payload) !== null && _result$payload !== void 0 && _result$payload.next_action);
      var isSuccess = isSuccessWithoutAccountingForNextActionBug && !isImpossibleSuccessState;
      dispatchGasV3TrackEvent({
        action: action,
        actionSubject: actionSubject,
        attributes: {
          success: isSuccess
        }
      });
      var taskId = function () {
        if (isPaymentAuthenticationFailureResult(result)) {
          // Need this because it's hard to know whether 3DS working in certain countries
          return 'authentication';
        } else if (isException(result) || isSystemErrorResult(result) || isImpossibleSuccessState) {
          return 'guaranteed-bug';
        } else {
          return 'other';
        }
      }();
      if (isFailure(result) || isImpossibleSuccessState) {
        var probabilityOfBug = isException(result) || isSystemErrorResult(result) || isImpossibleSuccessState ? ProbabilityOfBug.GUARANTEED : ProbabilityOfBug.MAYBE;
        var resultType = function () {
          if (isSuccessful(result)) {
            return 'success';
          } else if (isException(result)) {
            return 'exception';
          } else if (isError(result)) {
            return 'error';
          } else {
            return 'unknown';
          }
        }();
        var stripeErrorTags = isError(result) && result.error.type === ErrorTypes.STRIPE_ERROR ? mapStripeErrorToSentryTags(result.error.stripeError) : null;
        var tags = _objectSpread({
          // Both of these will make life easier when linking Sentry to events
          task: task,
          taskId: taskId,
          probabilityOfBug: probabilityOfBug,
          resultType: resultType,
          package: '@atlassian/commerce-credit-card-base'
        }, stripeErrorTags);
        dispatchSentryEvent({
          exception: isImpossibleSuccessState ? result.payload : getSentryExceptionFromResult(result),
          hint: undefined,
          scopeContext: {
            tags: tags
          }
        });
      }
      eventDispatch(eventPayload);
    }, [dispatchSentryEvent, dispatchGasV3TrackEvent, eventDispatch]);
    return telemetryDispatch;
  };
  return useSubmissionTelemetryDispatch;
};
export var useErrorBoundaryEventDispatch = function useErrorBoundaryEventDispatch() {
  var dispatchSentry = useSentryExceptionDispatch();
  var dispatchBaseEvent = useBaseErrorBoundaryEventDispatch();
  return useCallback(function (error) {
    dispatchSentry({
      exception: error
    });
    dispatchBaseEvent(error);
  }, [dispatchSentry, dispatchBaseEvent]);
};
export var CreditCardFormBreadcrumb = function CreditCardFormBreadcrumb(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/React.createElement(Breadcrumb, {
    name: CC_FORM
  }, children);
};
export var useFieldErrorEventDispatch = function useFieldErrorEventDispatch() {
  var dispatchBaseEvent = useBaseFieldErrorEventDispatch();
  var dispatchGasV3 = useGasV3TrackEventDispatch();
  return useCallback(function (fieldName) {
    dispatchBaseEvent(fieldName);
    dispatchGasV3({
      action: 'invalidated',
      actionSubject: 'textField',
      actionSubjectId: calcTextFieldActionSubjectId(fieldName)
    });
  }, [dispatchBaseEvent, dispatchGasV3]);
};
export var useCreatePaymentMethodEventDispatch = createUseSubmissionTelemetryDispatch({
  useBaseEventDispatch: useBaseCreatePaymentMethodEventDispatch,
  action: 'create',
  actionSubject: 'paymentMethod'
});
export var useConfirmCardPaymentResultEventDispatch = createUseSubmissionTelemetryDispatch({
  useBaseEventDispatch: useBaseConfirmCardPaymentResultEventDispatch,
  action: 'confirm',
  actionSubject: 'cardPayment'
});
export var useConfirmCardSetupResultEventDispatch = createUseSubmissionTelemetryDispatch({
  useBaseEventDispatch: useBaseConfirmCardSetupResultEventDispatch,
  action: 'confirm',
  actionSubject: 'cardSetup'
});